import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { Pager } from '@/components/pager';
import { Badge, Callout, Field, buttonClass, inputClass } from '@/components/ui';
import { MoveStock } from '@/components/admin/move-stock';
import { ChangeProduct } from '@/components/admin/stock-matching';
import { PhotoGallery, photosOf } from '@/components/photo-viewer';
import { RecordMovement } from '@/components/admin/record-movement';
import {
  adminLocationsQuery, adminSuppliersQuery, itemCostHistoryQuery, itemQuery, itemSuppliersQuery, stockItemQuery,
  itemMovementsQuery,
} from '@/queries';
import { ColourShopControls } from '@/components/admin/colour-shop';
import { exGst, formatCents, formatMicro } from '@/lib/money';
import { CLASS_MEANS, CLASS_WORD, STOCK_CLASSES, isStockClass, type StockClass } from '@/lib/stock-class';
import type { ItemCostChange, Movement, MovementReason, StockItemView } from '@/lib/stock-types';

/**
 * One item: where it sits, and everything that has ever happened to it.
 * Spec 003 US-2, task T055.
 *
 * The history is the ledger, so it is append-only and complete. Nothing here is
 * ever edited: a mistake appears as a later `corrected` row pointing at the one
 * it fixes, and both stay. That is why a wrong count is safe to record.
 *
 * There is no cost column and there cannot be. `unit_cost_cents` is not granted
 * to a signed-in caller, so the route does not select it and this screen has
 * nothing to show even if it wanted to (R-90).
 */

const REASON_LABEL: Record<MovementReason, string> = {
  received: 'Received',
  picked: 'Picked for a job',
  wasted: 'Wasted',
  returned: 'Returned',
  counted: 'Stocktake adjustment',
  written_off: 'Written off',
  transferred: 'Transferred',
  corrected: 'Correction',
  reserved_release: 'Reservation released',
};

/** Which of these a person could have typed. The rest are written by the system. */
const BY_HAND: MovementReason[] = ['wasted', 'returned', 'transferred', 'written_off', 'corrected'];

function origin(m: Movement): string | null {
  if (m.receipt_id) return 'from a goods receipt';
  if (m.job_id) return 'from a job';
  if (m.take_id) return 'from an approved stocktake';
  if (m.order_id) return 'from an order';
  if (m.corrects_id) return 'correcting an earlier movement';
  return null;
}

/**
 * The route. It reads the URL and nothing else.
 *
 * The BODY takes its id as a prop, because the same facts are drawn in two
 * places now: on this page, and in the panel over the stock list. A component
 * that reads `useParams` can only ever be the whole page.
 */
export default function AdminStockItemPage() {
  // The id reads '/admin/admin/...' because the admin shell is a PATHLESS
  // layout route with id 'admin', so the router prefixes its id and the child
  // carries the full path as well. The URL is /admin/stock/<id>.
  const { itemId } = useParams({ from: '/admin/admin/stock/$itemId' });
  return <StockItem itemId={itemId} />;
}

export function StockItem(
  { itemId, embedded = false }: { itemId: string; embedded?: boolean },
) {
  // The ledger for one item is append only and never shortens, so it is a page
  // like everything else. It used to ask for 200 and call that the history.
  const [page, setPage] = useState(1);
  const movementPage = useSuspenseQuery(itemMovementsQuery(itemId, page)).data;
  const movements = movementPage.rows;
  // Every shelf THIS item sits on, asked for by id.
  //
  // This used to read the whole stock list and filter it in the browser. That
  // worked while the list was one response; once the list became a page of 50
  // it found the item only when the item happened to be on page one. R-231.
  const { rows: here } = useSuspenseQuery(stockItemQuery(itemId)).data;
  // The item itself, asked for by id. It used to come from `here[0]`, which is
  // empty for an item with no stock anywhere: a new style's items, until their
  // first delivery. Those are exactly the items that need a supplier and a
  // reorder point set before anybody can order them. Spec 010.
  const view = useSuspenseQuery(itemQuery(itemId)).data;
  const item = view.item;
  const totalFree = here.reduce((sum, r) => sum + Number(r.free), 0);
  const totalOnHand = here.reduce((sum, r) => sum + Number(r.on_hand), 0);
  const totalReserved = here.reduce((sum, r) => sum + Number(r.reserved), 0);

  return (
    <div className={embedded ? 'px-4 py-4' : ''}>
      {/* The panel has its own Close, and a second way out beside it reads as
          two different things to do. */}
      {!embedded && (
        <Link to="/admin/stock" className="text-[14px] text-muted">
          ← All stock
        </Link>
      )}

      <div className="mb-6 mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">
            {item?.name ?? 'Item'}
            {/* Spec 014, FR-1403. An archived item is hidden from the lists, so
                when one is open it says so. */}
            {item?.line_status === 'archived' && <Badge tone="warn" className="ml-2 align-middle">Archived</Badge>}
          </h1>
          <p className="mt-1 font-mono text-[14px] text-muted">{item?.code ?? itemId}</p>
          {item?.product_variants?.product_id && (
            <Link
              to="/admin/styles/$productId"
              params={{ productId: item.product_variants.product_id }}
              className="mt-1 inline-block text-[12.5px] text-brand-ink underline"
            >
              Open the style: colours, prices and every size
            </Link>
          )}
        </div>
        {/* Tracker #1. Guy asked how to edit a stock item and the answer was
            that he could not. */}
        {item && (
          <div className="flex flex-wrap items-start gap-2">
            {/* Spec 011, FR-1121. A garment is one colour and size of one
                product, and the Shopify import often chose the wrong one. */}
            {item.product_variants?.product_id && (
              <ChangeProduct
                itemId={itemId}
                current={{
                  product_id: item.product_variants.product_id,
                  colour: item.product_variants.colour ?? '',
                  size: item.product_variants.size ?? '',
                }}
              />
            )}
            <EditItem itemId={itemId} item={item} productId={item.product_variants?.product_id} />
          </div>
        )}
      </div>

      {item && <ItemFacts itemId={itemId} view={view} />}

      {/* Spec 013, FR-1324. An item can come from many suppliers. */}
      {item && <ItemSuppliers itemId={itemId} />}

      {/* Spec 012, FR-1273. Every change of the average cost, and why. */}
      {item && view.cost && <ItemCostHistory itemId={itemId} />}

      {/* Spec 008 FR-807. The panel had no photo at all, while the list beside
          it had a 24 px one. Guy, 5 October 2026: he needs it bigger, and every
          photo of the product, not only the first. */}
      {item && photosOf(item.product_variants, item.name).length > 0 && (
        <div className="mb-6 max-w-[420px]">
          <PhotoGallery photos={photosOf(item.product_variants, item.name)} title={item.name} />
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {[
          ['Free', totalFree, 'what a job can actually use'],
          ['On hand', totalOnHand, 'what is physically on the shelves'],
          ['Reserved', totalReserved, 'promised to orders not yet picked'],
        ].map(([label, value, blurb]) => (
          <div key={String(label)} className="rounded-[10px] border border-line bg-panel p-4">
            <p className="text-[13px] uppercase tracking-wide text-muted">{label}</p>
            <p className="mt-1 text-[26px] font-extrabold tracking-[-0.02em]">
              {Number(value)}{' '}
              <span className="text-[15px] font-normal text-muted">{item?.base_uom}</span>
            </p>
            <p className="mt-1 text-[13px] text-muted">{blurb}</p>
          </div>
        ))}
      </div>

      {here.length > 1 && (
        <div className="mb-6 rounded-[10px] border border-line bg-panel">
          <p className="border-b border-line px-4 py-2 text-[12px] font-semibold uppercase tracking-wide text-muted">
            Across {here.length} locations
          </p>
          {here.map((r) => (
            <div
              key={r.location_id}
              className="flex items-center justify-between border-b border-line px-4 py-2 text-[14px] last:border-0"
            >
              <span>{r.locations?.code} <span className="text-muted">{r.locations?.label}</span></span>
              <span className="text-muted">
                {Number(r.free)} free of {Number(r.on_hand)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Two acts, and they are different acts. An adjustment changes how much
          there is; a move changes where it is and changes the total by nothing.
          Recording a move as two adjustments is how a bay ends up holding stock
          that is really in the other bay. T048, FR-111. */}
      <div className="mb-5 flex flex-wrap items-start gap-3">
        <RecordMovement itemId={itemId} uom={item?.base_uom ?? 'ea'} />
        <MoveStock itemId={itemId} uom={item?.base_uom ?? 'ea'} />
      </div>

      <h2 className="mb-3 text-[18px] font-bold">History</h2>

      {movements.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[15px] text-muted">
            Nothing has happened to this item yet.
          </p>
        </div>
      ) : (
        <div className="rounded-[10px] border border-line bg-panel">
          {movements.map((m) => {
            const qty = Number(m.qty);
            const where = origin(m);
            return (
              <div
                key={m.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1
                           border-b border-line px-4 py-3 text-[14px] last:border-0"
              >
                <span className="flex items-baseline gap-3">
                  <span
                    className={`w-20 text-right font-semibold ${
                      qty < 0 ? 'text-muted' : ''
                    }`}
                  >
                    {qty > 0 ? `+${qty}` : qty}
                  </span>
                  <span>{REASON_LABEL[m.reason]}</span>
                  {!BY_HAND.includes(m.reason) && (
                    <Badge tone="ok">system</Badge>
                  )}
                </span>
                <span className="text-[13px] text-muted">
                  {where && <>{where} · </>}
                  {new Date(m.at).toLocaleString('en-AU')}
                </span>
                {m.note && (
                  <span className="w-full text-[13px] text-muted">{m.note}</span>
                )}
              </div>
            );
          })}
          <Pager
            page={movementPage.page}
            pages={movementPage.pages}
            total={movementPage.total}
            limit={movementPage.limit}
            noun="movement"
            onPage={setPage}
          />
        </div>
      )}

      <p className="mt-4 max-w-[62ch] text-[13px] text-muted">
        This list is never edited. A mistake is fixed by a new correction row
        pointing at the one it corrects, and both stay on the record, so the
        history always explains how the current number was reached.
      </p>
    </div>
  );
}

/**
 * EDITING A STOCK ITEM. Tracker #1.
 *
 * Guy: "Can you tell me how I edit Stock items please, Code, Description,
 * Photo, Cost or sale Price, etc."
 *
 * Two of those had nowhere to be changed at all, and they are here now: the
 * code and the description. The reorder figures come with them, because the
 * Reorder screen showed both and could change neither.
 *
 * The other three are somewhere else ON PURPOSE, and the screen says where
 * rather than leaving him to hunt:
 *
 *   Photo       Products, with the rest of what the shop says about a garment.
 *   Sale price  the shop row of the colour. Since spec 014 (FR-1420) it is
 *               changed in ItemFacts, above this form, with the same control
 *               as the style page.
 *   Cost        the weighted average of what was paid, from goods receipts.
 *               Since 18 October somebody with costs.edit can also type one,
 *               because Guy asked for it (tracker #9) and some items have no
 *               receipt. See ItemFacts and migration 294.
 */
function EditItem(
  { itemId, item, productId }:
  { itemId: string; item: { code: string; name: string;
                            reorder_point?: number | null; reorder_qty?: number | null;
                            max_level?: number | null;
                            preferred_supplier_id?: string | null;
                            line_status?: StockClass };
    productId?: string },
) {
  const qc = useQueryClient();
  const ownedByStyle = Boolean(productId);
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState(item.preferred_supplier_id ?? '');
  // Who to buy it from. Reorder groups items by it, and without one an item
  // sits under "No supplier set yet" where no order can be raised. Spec 010.
  const [code, setCode] = useState(item.code ?? '');
  const [name, setName] = useState(item.name ?? '');
  const [point, setPoint] = useState(String(item.reorder_point ?? ''));
  const [qty, setQty] = useState(String(item.reorder_qty ?? ''));
  const [max, setMax] = useState(item.max_level == null ? '' : String(item.max_level));
  const [lineStatus, setLineStatus] = useState<StockClass>(item.line_status ?? 'stock_line');
  const [error, setError] = useState<string | null>(null);

  const supplierList = useQuery({ ...adminSuppliersQuery, enabled: open, retry: false });
  const suppliers = supplierList.data?.suppliers ?? [];
  // Staff hold inventory and not purchasing, so the list is refused to them.
  // They are told who sets it, rather than shown a list with nothing in it.
  const maySeeSuppliers = !supplierList.isError;

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/items/${itemId}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          code,
          name,
          reorder_point: point.trim() === '' ? null : Number(point),
          reorder_qty: qty.trim() === '' ? null : Number(qty),
          max_level: max.trim() === '' ? null : Number(max),
          line_status: lineStatus,
          // Only from somebody shown the list. A staff save would otherwise
          // send "none" and clear the supplier a buyer had set.
          ...(maySeeSuppliers && supplierList.isSuccess
            ? { preferred_supplier_id: supplier === '' ? null : supplier }
            : {}),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Could not save (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['stock-item', itemId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'low-stock'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'suppliers'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-[10px] border border-line px-3 py-2 text-[13px] font-semibold"
      >
        Edit this item
      </button>
    );
  }

  return (
    <form
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Code" htmlFor="item-code">
          <input
            id="item-code" className={inputClass} value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Description" htmlFor="item-name">
          {/* A garment's description is its style, colour and size, and the
              style page renames them all together. Tracker #1. */}
          <input
            id="item-name" className={inputClass} value={name} readOnly={ownedByStyle}
            onChange={(e) => setName(e.target.value)}
          />
          {ownedByStyle && (
            <span className="mt-1 block text-[12px] text-muted">
              Set by the style. Rename the style on its page in Products.
            </span>
          )}
        </Field>
        {/* Spec 014, FR-1410. The reorder point is the Minimum: Reorder lists
            the item when free stock falls to it. */}
        <Field
          label="Minimum" htmlFor="item-point" optional
          hint="Reorder when free stock falls to this"
        >
          <input
            id="item-point" className={inputClass} value={point} inputMode="decimal"
            onChange={(e) => setPoint(e.target.value)}
          />
        </Field>
        <Field
          label="Maximum" htmlFor="item-max" optional
          hint="Reorder suggests enough to come up to this"
        >
          <input
            id="item-max" className={inputClass} value={max} inputMode="decimal"
            onChange={(e) => setMax(e.target.value)}
          />
        </Field>
        <Field
          label="Reorder quantity" htmlFor="item-qty" optional
          hint="Ordered when there is no maximum"
        >
          <input
            id="item-qty" className={inputClass} value={qty} inputMode="decimal"
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>
        {/* Spec 014, FR-1401. Only a Stock line is reordered. An item with
            stock cannot be archived: the server says how many are where. */}
        <Field label="Class" htmlFor="item-line-status">
          <select
            id="item-line-status" className={inputClass} value={lineStatus}
            onChange={(e) => setLineStatus(e.target.value as StockClass)}
          >
            {STOCK_CLASSES.map((c) => (
              <option key={c} value={c}>{CLASS_WORD[c]}: {CLASS_MEANS[c]}</option>
            ))}
          </select>
        </Field>
        {maySeeSuppliers ? (
        <Field
          label="Main supplier" htmlFor="item-supplier" optional
          hint="Buy it from this supplier. Reorder groups items by it"
        >
          <select
            id="item-supplier" className={inputClass} value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
          >
            <option value="">No supplier set yet</option>
            {/* A switched off supplier is not offered (spec 013, FR-1304),
                except the one already set, so the form does not clear it. */}
            {suppliers.filter((sp) => sp.active || sp.id === item.preferred_supplier_id).map((sp) => (
              <option key={sp.id} value={sp.id}>{sp.name}{sp.active ? '' : ' (switched off)'}</option>
            ))}
          </select>
        </Field>
        ) : (
          <p className="text-[12.5px] text-muted sm:col-span-2">
            Who to buy it from is set by somebody with the purchasing permission.
          </p>
        )}
      </div>

      {error && <Callout tone="danger" title={error} className="mt-3" />}

      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </button>
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setError(null); }}
        >
          Cancel
        </button>
      </div>

      {/*
        * SAY WHERE THE OTHERS ARE. Guy asked for five things in one sentence,
        * and three of them live elsewhere for good reasons. Leaving him to
        * find that out by looking is how the same question gets asked twice.
        */}
      <div className="mt-4 border-t border-line pt-3 text-[12.5px] text-muted">
        <p className="font-semibold text-ink">The rest of it lives elsewhere</p>
        <ul className="mt-1.5 space-y-1">
          {/* Spec 010: one page holds the style's photos, words, colours,
              prices and the stock of every size. */}
          <li>
            {productId ? (
              <Link
                to="/admin/styles/$productId"
                params={{ productId }}
                className="underline"
              >
                Open the style
              </Link>
            ) : 'The style page'}
            {' '}holds the photographs, what the shop says about it, the price of
            each colour, and the stock of every size.
          </li>
          <li>
            The cost comes from your goods receipts. Somebody with the costs
            edit permission can type one under Cost, above this form.
          </li>
        </ul>
      </div>
    </form>
  );
}

/**
 * What Guy asked to see before he edits a stock item. Tracker #1, 16 October:
 * "1.The Sale Price 2. The Cost Price 3.The Supplier 4. The Brand".
 *
 * The sale price shows with and without GST, because he asked for it
 * "excluding GST" and the shop shows it with. A colour not in the shop says
 * so, and links to the style page where "Put in the shop" is.
 *
 * The cost row is absent for somebody without costs.view, not $0.00. "No cost
 * yet" and a typed $0.00 are different answers (migration 294).
 */
function ItemFacts({ itemId, view }: { itemId: string; view: StockItemView }) {
  const qc = useQueryClient();
  const { item, listing, supplier, cost } = view;
  if (!item) return null;
  const v = item.product_variants;
  const p = v?.products;
  const rows: Array<[string, React.ReactNode]> = [];
  // Spec 014, FR-1406. The class decides whether Reorder suggests the item.
  const cls = isStockClass(item.line_status) ? item.line_status : 'stock_line';
  rows.push(['Class', `${CLASS_WORD[cls]}: ${CLASS_MEANS[cls]}`]);
  if (v) rows.push(['Colour and size', `${v.colour} / ${v.size}`]);
  // Spec 014, FR-1421. What the product says about itself, read only here:
  // it is the product's to change, on its page.
  if (v && p) {
    const facts = [p.category, p.material, p.weight_gsm != null ? `${p.weight_gsm} GSM` : null, p.fit]
      .filter(Boolean).join(' · ');
    rows.push(['Product', (
      <span key="product">
        {p.name}{facts && <span className="text-muted"> · {facts}</span>}{' '}
        {v.product_id && (
          <Link to="/admin/styles/$productId" params={{ productId: v.product_id }} className="text-[13px] underline">
            Open the product
          </Link>
        )}
      </span>
    )]);
  }
  if (v) {
    rows.push(['Sale price', (
      <span key="price" className="block space-y-1">
        {listing ? (
          <span className="block">
            {formatCents(exGst(listing.retail_price_cents))} without GST
            <span className="text-muted"> ({formatCents(listing.retail_price_cents)} with GST)</span>
            {listing.active ? <span className="text-muted">. In the online shop.</span>
              : <span className="text-muted">. Out of the online shop.</span>}
          </span>
        ) : (
          <span className="block text-muted">This colour is not in the shop yet.</span>
        )}
        {/* Spec 014, FR-1420 and FR-1423. The same control as the style page,
            on the same shop row. The price is per colour, not per size. */}
        {v.product_id && (
          <ColourShopControls
            key={`${listing?.id ?? 'none'}-${listing?.retail_price_cents ?? ''}-${listing?.active ?? ''}`}
            productId={v.product_id}
            colour={v.colour}
            listing={listing}
            may={{ edit: view.may?.price ?? false, create: view.may?.publish ?? false }}
            onDone={() => {
              void qc.invalidateQueries({ queryKey: ['stock-item', itemId] });
              void qc.invalidateQueries({ queryKey: ['admin', 'style', v.product_id] });
            }}
          />
        )}
        {(view.may?.price || view.may?.publish) && (
          <span className="block text-[12px] text-muted">This price is for every size of {v.colour}.</span>
        )}
      </span>
    )]);
    rows.push(['Brand', listing?.brand ?? <span className="text-muted">Set when the colour goes in the shop</span>]);
  }
  rows.push(['Minimum and maximum', (
    <span key="levels">
      {Number(item.reorder_point ?? 0) > 0 ? `Reorder at ${Number(item.reorder_point)}` : <span className="text-muted">No minimum</span>}
      {item.max_level != null && <>, up to {Number(item.max_level)}</>}
    </span>
  )]);
  rows.push(['Main supplier', supplier?.name ?? <span className="text-muted">No supplier set yet</span>]);
  if (cost) rows.push(['Cost', <CostValue key="cost" itemId={itemId} cost={cost} />]);

  return (
    <dl className="mb-6 grid max-w-[720px] gap-x-6 gap-y-2 rounded-[10px] border border-line bg-panel p-4 text-[14px] sm:grid-cols-[160px_1fr]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-[13px] text-muted">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const SOURCE_WORDS = { receipt: 'from goods received', typed: 'typed in' } as const;

const CAUSE_WORDS: Record<ItemCostChange['cause'], string> = {
  receipt: 'Delivery',
  typed: 'Typed in',
  landed: 'Freight or duty',
  return: 'Return',
  migration: 'Cost on 30 Oct 2026',
};

/**
 * THE COST HISTORY. Spec 012, FR-1273: each delivery, typed cost and landed
 * cost, with the old and the new average to four decimals of a cent (FR-1270),
 * the cause and who. Only for somebody with costs.view, as the cost is.
 */
function ItemCostHistory({ itemId }: { itemId: string }) {
  const list = useQuery({ ...itemCostHistoryQuery(itemId), retry: false });
  if (list.isError || !list.data) return null;
  const rows = list.data.rows;
  return (
    <div className="mb-6 max-w-[720px] rounded-[10px] border border-line bg-panel">
      <p className="border-b border-line px-4 py-2 text-[12px] font-semibold uppercase tracking-wide text-muted">
        Cost history
      </p>
      {rows.length === 0 && <p className="px-4 py-3 text-[14px] text-muted">No cost change yet.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-muted">
                <th className="px-4 py-2 font-semibold">Date</th>
                <th className="px-2 py-2 font-semibold">Cause</th>
                <th className="px-2 py-2 text-right font-semibold">Before</th>
                <th className="px-2 py-2 text-right font-semibold">After</th>
                <th className="px-4 py-2 font-semibold">Who</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="px-4 py-2">{new Date(r.at).toLocaleDateString('en-AU')}</td>
                  <td className="px-2 py-2">
                    {CAUSE_WORDS[r.cause]}{r.po_ref ? <span className="text-muted"> {r.po_ref}</span> : null}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    {r.old_avg_cost_micro == null ? <span className="text-muted">None</span> : formatMicro(r.old_avg_cost_micro)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{formatMicro(r.new_avg_cost_micro)}</td>
                  <td className="px-4 py-2 text-muted">{r.actor_name ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CostValue(
  { itemId, cost }: { itemId: string; cost: NonNullable<StockItemView['cost']> },
) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(cost.cents == null ? '' : (cost.cents / 100).toFixed(2));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const dollars = text.trim().replace(/^\$/, '');
      if (!/^\d+(\.\d{1,2})?$/.test(dollars)) throw new Error('Type a cost in dollars, like 8.50 or 0.');
      const res = await fetch(`/api/items/${itemId}/cost`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ cost_cents: Math.round(Number(dollars) * 100) }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Could not save (${res.status})`);
      }
    },
    onSuccess: () => {
      setError(null);
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['stock-item', itemId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!editing) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
        {cost.cents == null
          ? <span className="text-muted">No cost yet</span>
          : <span>{formatCents(cost.cents)} <span className="text-muted">each, {SOURCE_WORDS[cost.source!]}</span></span>}
        {cost.may_edit && (
          <button type="button" className="text-[13px] font-semibold underline" onClick={() => setEditing(true)}>
            {cost.cents == null ? 'Set a cost' : 'Change'}
          </button>
        )}
      </span>
    );
  }

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <label htmlFor="item-cost" className="sr-only">Cost each, without GST</label>
      <input
        id="item-cost" className={`${inputClass} w-[120px]`} value={text} inputMode="decimal"
        placeholder="0.00" onChange={(e) => setText(e.target.value)} autoFocus
      />
      <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
        {save.isPending ? 'Saving' : 'Save'}
      </button>
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => { setEditing(false); setError(null); }}>
        Cancel
      </button>
      <span className="basis-full text-[12px] text-muted">
        Each, without GST. 0 means it costs nothing. The next goods receipt averages with this figure.
      </span>
      {error && <span role="alert" className="basis-full text-[13px] text-danger">{error}</span>}
    </form>
  );
}

/**
 * THE SUPPLIERS OF THIS ITEM. Spec 013, FR-1324.
 *
 * An item can come from many suppliers, and one of them is the main supplier
 * ("Buy it from"), which Reorder groups by. Each shows its own code for the
 * item and the last price paid to it, for somebody who can see costs.
 *
 * Staff hold inventory and not purchasing, so the list is refused to them.
 * They are told who sets it, rather than shown a list with nothing in it.
 */
function ItemSuppliers({ itemId }: { itemId: string }) {
  const qc = useQueryClient();
  const list = useQuery({ ...itemSuppliersQuery(itemId), retry: false });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['stock-item', itemId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'suppliers'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
  };

  const makeMain = useMutation({
    mutationFn: async (supplierId: string) => {
      const res = await fetch(`/api/items/${itemId}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ preferred_supplier_id: supplierId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Could not save (${res.status})`);
      }
    },
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  const unlink = useMutation({
    mutationFn: async (supplierId: string) => {
      const res = await fetch(`/api/suppliers/${supplierId}/items/${itemId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Could not remove (${res.status})`);
      }
    },
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  if (list.isError) {
    return (
      <p className="mb-6 text-[12.5px] text-muted">
        The suppliers of an item show to somebody with the purchasing permission.
      </p>
    );
  }
  if (!list.data) return null;
  const { rows, costs_visible: costsVisible, may_edit: mayEdit } = list.data;

  return (
    <div className="mb-6 max-w-[720px] rounded-[10px] border border-line bg-panel">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-muted">Suppliers</p>
        {mayEdit && !adding && (
          <button type="button" className="text-[13px] font-semibold underline" onClick={() => setAdding(true)}>
            Add a supplier
          </button>
        )}
      </div>
      {adding && (
        <AddItemSupplier
          itemId={itemId}
          linked={rows.map((r) => r.supplier_id)}
          onDone={() => { setAdding(false); refresh(); }}
        />
      )}
      {rows.length === 0 && !adding && (
        <p className="px-4 py-3 text-[14px] text-muted">
          No supplier yet. Add one, or receive this item on a purchase order.
        </p>
      )}
      {rows.map((r) => (
        <div
          key={r.supplier_id}
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-line px-4 py-2 text-[14px] last:border-0"
        >
          <span className="flex flex-wrap items-center gap-2">
            <Link to="/admin/suppliers/$supplierId" params={{ supplierId: r.supplier_id }} className="font-semibold underline">
              {r.name ?? 'Supplier'}
            </Link>
            {r.main && <Badge tone="ok">main supplier</Badge>}
            {r.supplier_active === false && <Badge tone="warn">switched off</Badge>}
            <span className="font-mono text-[13px] text-muted">{r.supplier_code ?? ''}</span>
          </span>
          <span className="flex flex-wrap items-center gap-3 text-[13px]">
            {costsVisible && (
              <span className="text-muted">
                {r.last_price_cents == null
                  ? 'Not bought yet'
                  : `Last paid ${formatCents(r.last_price_cents)} on ${new Date(r.last_price_at!).toLocaleDateString('en-AU')}`}
              </span>
            )}
            {mayEdit && !r.main && (
              <>
                <button
                  type="button" className="underline" disabled={makeMain.isPending}
                  onClick={() => makeMain.mutate(r.supplier_id)}
                >
                  Make main
                </button>
                <button
                  type="button" className="underline" disabled={unlink.isPending}
                  onClick={() => unlink.mutate(r.supplier_id)}
                >
                  Remove
                </button>
              </>
            )}
          </span>
        </div>
      ))}
      {error && <p role="alert" className="px-4 py-2 text-[13px] text-danger">{error}</p>}
    </div>
  );
}

function AddItemSupplier(
  { itemId, linked, onDone }: { itemId: string; linked: string[]; onDone: () => void },
) {
  const all = useQuery({ ...adminSuppliersQuery, retry: false });
  const offer = (all.data?.suppliers ?? []).filter((s) => s.active && !linked.includes(s.id));
  const [supplierId, setSupplierId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/suppliers/${supplierId}/items`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, supplier_code: code }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Could not add (${res.status})`);
      }
    },
    onSuccess: () => { setError(null); onDone(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3"
      onSubmit={(e) => { e.preventDefault(); if (supplierId) add.mutate(); }}
    >
      <Field label="Supplier" htmlFor="add-item-supplier">
        <select
          id="add-item-supplier" className={`${inputClass} w-[220px]`} value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
        >
          <option value="">Choose a supplier</option>
          {offer.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label="Supplier's code" htmlFor="add-item-supplier-code" optional>
        <input
          id="add-item-supplier-code" className={`${inputClass} w-[180px]`} value={code} maxLength={60}
          onChange={(e) => setCode(e.target.value)}
        />
      </Field>
      <button type="submit" className={buttonClass('primary', 'sm')} disabled={!supplierId || add.isPending}>
        {add.isPending ? 'Adding' : 'Add'}
      </button>
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={onDone}>Cancel</button>
      {error && <p role="alert" className="basis-full text-[13px] text-danger">{error}</p>}
    </form>
  );
}
